import { motion } from "framer-motion";

const Bar = ({ w = "100%", h = 8, c = "#2a2a30" }) => (
  <div style={{ width: w, height: h, background: c, borderRadius: 99 }} />
);

const Chrome = ({ children, url }) => (
  <div className="rounded-xl overflow-hidden border border-zinc-800 bg-[#0d0d10] text-left">
    <div className="flex items-center gap-2 px-3 py-2.5 bg-[#141419] border-b border-zinc-800">
      <span className="w-2.5 h-2.5 rounded-full bg-[#3a3a42]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#3a3a42]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#3a3a42]" />
      <span className="ml-3 flex-1 truncate rounded-md bg-[#1c1c22] px-3 py-1 font-mono text-[10px] text-zinc-500">{url}</span>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const SiteHeader = ({ name, accent }) => (
  <div className="flex items-center justify-between mb-5">
    <div className="flex items-center gap-2">
      <div style={{ width: 22, height: 22, borderRadius: 6, background: accent }} />
      <span className="font-serif text-sm text-[#F8F6F0]">{name}</span>
    </div>
    <div className="flex items-center gap-3">
      <Bar w={34} h={6} />
      <Bar w={34} h={6} />
      <div style={{ background: accent, borderRadius: 99, padding: "5px 12px" }}>
        <span className="font-sans text-[9px] font-semibold text-[#0d0d10]">Get a Quote</span>
      </div>
    </div>
  </div>
);

const HeroBlock = ({ accent, headline, cta }) => (
  <div className="rounded-lg p-5 mb-4" style={{ background: `linear-gradient(135deg, #17171c, #101013)`, border: `1px solid ${accent}33` }}>
    <div style={{ width: 46, height: 5, background: accent, borderRadius: 99, marginBottom: 10 }} />
    <p className="font-serif text-lg leading-snug text-[#F8F6F0] mb-1.5">{headline}</p>
    <div className="space-y-1.5 mb-3">
      <Bar w="82%" h={5} c="#3a3a42" />
      <Bar w="64%" h={5} c="#3a3a42" />
    </div>
    <div style={{ display: "inline-block", background: accent, borderRadius: 99, padding: "6px 14px" }}>
      <span className="font-sans text-[9px] font-semibold text-[#0d0d10]">{cta}</span>
    </div>
  </div>
);

const CardGrid = ({ items, accent, cols = 3 }) => (
  <div className={`grid gap-2.5 mb-4`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
    {items.map((s) => (
      <div key={s} className="rounded-md border border-zinc-800 bg-[#121216] p-3">
        <div style={{ width: 16, height: 16, borderRadius: 5, background: `${accent}55`, marginBottom: 7 }} />
        <p className="font-sans text-[9px] font-semibold text-zinc-300 leading-tight">{s}</p>
        <div className="mt-1.5"><Bar w="90%" h={4} c="#33333b" /></div>
      </div>
    ))}
  </div>
);

const GalleryBlock = ({ accent }) => (
  <div className="grid grid-cols-3 gap-2.5 mb-4">
    {[0.85, 0.65, 0.75].map((o, i) => (
      <div key={i} className="rounded-md h-14" style={{ background: `linear-gradient(135deg, ${accent}${Math.round(o * 40).toString(16).padStart(2, "0")}, #1a1a20)` }} />
    ))}
  </div>
);

const FormBlock = ({ accent }) => (
  <div className="rounded-lg border border-zinc-800 bg-[#121216] p-4 mb-4">
    <p className="font-sans text-[10px] font-semibold text-zinc-300 mb-2.5">Request an Estimate</p>
    <div className="grid grid-cols-2 gap-2 mb-2">
      <div className="rounded-md border border-zinc-800 bg-[#0d0d10] px-2.5 py-2"><Bar w="70%" h={4} c="#3a3a42" /></div>
      <div className="rounded-md border border-zinc-800 bg-[#0d0d10] px-2.5 py-2"><Bar w="55%" h={4} c="#3a3a42" /></div>
    </div>
    <div style={{ background: accent, borderRadius: 8, padding: "6px 0", textAlign: "center" }}>
      <span className="font-sans text-[9px] font-semibold text-[#0d0d10]">Get My Free Quote</span>
    </div>
  </div>
);

const FaqBlock = () => (
  <div className="space-y-2 mb-1">
    {["Licensed & Insured", "Service Area", "Financing Options"].map((q) => (
      <div key={q} className="flex items-center justify-between rounded-md border border-zinc-800 bg-[#121216] px-3 py-2.5">
        <span className="font-sans text-[9px] text-zinc-400">{q}</span>
        <span className="text-zinc-600 text-[10px]">+</span>
      </div>
    ))}
  </div>
);

export default function SiteMockup({ demo, tier }) {
  const accent = demo.accent;
  const url = `https://www.${demo.demoBusiness.toLowerCase().replace(/[^a-z]/g, "")}.com`;
  const headline = {
    starter: `${demo.trade} done right.`,
    growth: `Book your ${demo.trade.toLowerCase()} estimate in 60 seconds.`,
    premium: `The ${demo.trade.toLowerCase()} team your neighbors recommend.`,
  }[tier];
  const cta = tier === "starter" ? "Call Now" : "Get a Free Quote";

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Chrome url={url}>
        <SiteHeader name={demo.demoBusiness} accent={accent} />
        <HeroBlock accent={accent} headline={headline} cta={cta} />
        <CardGrid items={demo.services} accent={accent} cols={tier === "starter" ? 3 : 3} />
        {tier !== "starter" && <GalleryBlock accent={accent} />}
        {tier !== "starter" && <FormBlock accent={accent} />}
        {tier === "premium" && <FaqBlock />}
        <div className="flex items-center justify-between pt-3 mt-2 border-t border-zinc-800/60">
          <Bar w={70} h={5} c="#33333b" />
          <Bar w={90} h={5} c="#33333b" />
        </div>
      </Chrome>
    </motion.div>
  );
}
